import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { Editions, ProductKeys } from "@atlassiansox/asc-stargate";

import { useCrossFlowFrontendTouchpoint } from "@/components/cross-flow/touchpoint";
import ContentBlock from "@/components/wpl/content-block/src";
import { PictureProps } from "@/components/wpl/image/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";

const ctaSectionStyle = css({
    "paddingBlockStart": "64px",
    "paddingBlockEnd": "64px",
    "backgroundImage":
        "url(https://wac-cdn.atlassian.com/misc-assets/webp-images/GWP-1150-footerbg.webp), url(https://wac-cdn.atlassian.com/misc-assets/webp-images/GWP-1150-tablet-footerbg.webp)",
    "backgroundRepeat": "no-repeat , no-repeat",
    "backgroundPosition": "bottom -2px right , bottom 480px left",
    "backgroundSize": "200px , 218px",
    [media.above.xs]: {
        backgroundSize: "218px , 200px"
    },
    [media.above.sm]: {
        backgroundPosition: "bottom -2px right , bottom -50px left",
        backgroundSize: "300px , 318px"
    },

    ".cta-section-container": {
        padding: "16px"
    },
    ".jira-product-discovery-pronouncedCtaSection": {
        "padding": "40px",
        "marginBlockEnd": "32px",
        "backgroundColor": "#dfe1e5",
        "display": "grid",
        "gridTemplateColumns": "minmax(0, 1fr)",
        "gap": "1rem",
        [media.above.sm]: {
            gridTemplateColumns: "minmax(0, 0.65fr) minmax(0, 2fr)"
        },
        ".img-section": {
            display: "grid",
            justifyContent: "center",
            alignItems: "center",
            img: {
                width: "170px",
                paddingInlineStart: "20px",
                paddingInlineEnd: "20px"
            }
        },
        ".text-section": {
            "display": "grid",
            "alignContent": "center",
            "justifyItems": "flex-start",
            "padding": 0,

            ".contentBlock-linkFlex": {
                margin: 0
            }
        }
    },
    ".jira-product-discovery-cta": {
        "padding": "32px 16px 16px 16px",
        ".text-section": {
            padding: "16px",
            [media.above.md]: {
                padding: "0px"
            }
        }
    }
});

export type PronouncedCtaSectionProps = {
    readonly headerText: string;
    readonly bodyText: string;
    readonly pictureProps: PictureProps;
    readonly ctaText: string;
    readonly ctaHref: string;
};

export type CtaProps = {
    readonly headerText: string;
    readonly bodyText: string;
    readonly ctaText: string;
    readonly ctaHref: string;
};

export type CTASectionProps = {
    readonly pronouncedCtaSectionProps: PronouncedCtaSectionProps;
    readonly ctaProps: CtaProps;
};

const CTASection = ({
    pronouncedCtaSectionProps,
    ctaProps
}: CTASectionProps) => {
    const signupLink = useLocalizedHref(
        "/try/cloud/signup?bundle=jira-product-discovery"
    );
    const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
        productKey: ProductKeys.JIRA_PRODUCT_DISCOVERY,
        edition: Editions.FREE,
        redirectUrl: signupLink
    });
    return (
        <section className="cta-section" css={ctaSectionStyle}>
            <Row layout="10">
                <div className="cta-section-container">
                    <ContentBlock
                        className="jira-product-discovery-pronouncedCtaSection"
                        headerText={pronouncedCtaSectionProps.headerText}
                        headerType="h4"
                        bodyText={pronouncedCtaSectionProps.bodyText}
                        cardStyle={true}
                        orientation="horizontal"
                        pictureProps={pronouncedCtaSectionProps.pictureProps}
                        ctaProps={{
                            primaryLinkProps: {
                                text: pronouncedCtaSectionProps.ctaText,
                                type: "basic-underline",
                                doesArrowAnimate: true,
                                href: pronouncedCtaSectionProps.ctaHref,
                                className:
                                    "jira-product-discovery-join-the-discussion-section-cta",
                                dataEventProps: {
                                    "data-event-component": "linkButton",
                                    "data-event-container":
                                        "pronouncedCtaSection",
                                    "data-event": "clicked",
                                    "data-product-key": "jiraProductDiscovery"
                                }
                            }
                        }}
                    />
                    <ContentBlock
                        className="jira-product-discovery-cta"
                        headerText={ctaProps.headerText}
                        headerType="h2"
                        bodyText={ctaProps.bodyText}
                        isCentered={true}
                        ctaProps={{
                            buttonProps: {
                                text: ctaProps.ctaText,
                                color: "darkblue",
                                url: signupLink,
                                className:
                                    "jira-product-discovery-cta-section-cta",
                                dataEventProps: {
                                    "data-event-component": "linkButton",
                                    "data-event-container": "ctaSection",
                                    "data-event": "clicked",
                                    "data-product-key": "jiraProductDiscovery",
                                    "data-label": ctaProps.ctaText,
                                    "data-label-english": "Get it free",
                                    "data-track-event":
                                        "jiraProductDiscovery.get-it-free-button-clicked"
                                },
                                onClick: openCrossFlow
                            }
                        }}
                    />
                </div>
            </Row>
        </section>
    );
};

export default CTASection;
